<template>
  <div class="extravehicle">
    <div class="extravehicle__header">{{xsaleHeaderText}}</div>
    <div v-if="showLoadingIcon" class="card">
      <div class="card__loader">
        <LoaderComponent :show="showLoadingIcon" />
      </div>
    </div>
    <ul v-else class="extravehicle__list">
      <li v-for="vehicle in this.xsaleVehicles" class="card card--horizontal" :key="vehicle.rangeId" v-on:click.prevent="addVehicle(vehicle)">
        <div class="card__group">
          <img :src="imageUrl(vehicle)" />
          <span class="card__header" :class="{ 'card__header--active': isVehicleSelected(vehicle) }"> {{vehicle.manufacturerName}} {{vehicle.rangeName}}
          </span>
        </div>
        <div v-bind:class="{ 'card__indicator--active': isVehicleSelected(vehicle) }" class="card__indicator"></div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import {
  Component, Prop, Vue, Watch
} from 'vue-property-decorator';
import {
  State, Getter, Action, Mutation, namespace
} from 'vuex-class';
import OrderingPriority from '@/enums/OrderingPriority';
import VehicleType from '@/enums/VehicleType';
import Range from '@/models/vehicle/range';
import Vehicle from '@/models/vehicle/vehicle';
import { enquiryService } from '@/services/EnquiryService';
import VehicleStatusType from '@/enums/VehicleStatusType';
import IRange from '@/models/vehicle/interfaces/range.d';
import IVehicle from '@/models/vehicle/interfaces/vehicle.d';
import ExtraData from '@/models/ExtraData';
import LoaderComponent from '../../ui/LoaderComponent.vue';

@Component({
  components: {
  LoaderComponent
  }
  })
export default class PresubXsaleComponent extends Vue {
  @Prop({ default: OrderingPriority.TLASpecial })
  public orderingPriority!: OrderingPriority;

  @Prop({ default: 'Select to add to your enquiry' })
  public headerText!: string;

  @Prop({ default: VehicleStatusType.AdditionalUserRecommend })
  public vehicleStatusType!: VehicleStatusType;

  public xsaleVehicles: Vehicle[] = [];

  public showLoadingIcon: boolean = true;

  // Vuex

  public get currentRange(): IRange {
    return this.$store.getters['vehicleModule/currentRange'];
  }

  public get vehicles(): IVehicle[] {
    return this.$store.getters['vehicleManagementModule/extraVehicles'];
  }

  public addEnquiryVehicle(vehicle: Vehicle) {
    this.$store.dispatch('vehicleManagementModule/submitAddExtraVehicle', vehicle);
  }

  public removeEnquiryVehicle(vehicle: Vehicle) {
    this.$store.dispatch('vehicleManagementModule/submitRemoveExtraVehicle', vehicle);
  }

  // Component

  public addVehicle(selectedVehicle: Vehicle) {
    if (this.getAddedVehicles(selectedVehicle) === undefined && this.addedVehiclesCount !== 2) {
      this.addEnquiryVehicle(selectedVehicle);
      this.setXSale(selectedVehicle);
    } else if (this.getAddedVehicles(selectedVehicle) !== undefined) {
      this.removeEnquiryVehicle(selectedVehicle);
    }
  }

  public setXSale(selectedVehicle: Vehicle) {
    if (selectedVehicle !== null) {
      const extraData = new ExtraData('selectedXSaleVehicle', 'true');
      this.$store.dispatch('enquiryModule/submitExtraData', extraData);
    } else {
      this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'selectedXSaleVehicle');
    }
  }

  public get addedVehiclesCount() {
    return this.vehicles.length;
  }

  public isVehicleSelected(vehicle: Vehicle): boolean {
    if (this.addedVehiclesCount > 0 && this.getAddedVehicles(vehicle)) {
      return true;
    }

    return false;
  }

  public getAddedVehicles(vehicle: Vehicle) {
    return this.vehicles.find((x) => x.rangeId === vehicle.rangeId);
  }

  public get getRangeId(): number {
    if (this.currentRange == null) {
      return 0;
    }

    return this.currentRange.rangeId;
  }

  public get xsaleHeaderText(): string {
    if (this.showLoadingIcon) {
      return 'Currently Loading...';
    }

    return this.headerText;
  }

  @Watch('getRangeId')
  public async fetchSimilarVehicles() {
    if (this.currentRange !== null) {
      const relatedVehicles = await enquiryService.fetchRelatedVehicles(VehicleType.Car, this.orderingPriority, this.currentRange.rangeId);
      // We are limiting the number of similar vehicles to display to 4
      this.xsaleVehicles = relatedVehicles
        .slice(0, 4)
        .map((vehicle) => {
          vehicle.vehicleStatusType = 2;

          return vehicle;
        });
    }
  }

  public imageUrl(vehicle: Vehicle) {
    if (vehicle !== null) {
      if (vehicle.images.basic.length === 0) {
        return this.manufacturerLogo(vehicle);
      }
      return `//image.uk-tla.com/api/v1/image/vehicle/${vehicle.vehicleType}/${vehicle.manufacturerNameSlug}/${vehicle.rangeNameSlug}/basic/${vehicle.images.basic[0].imageId}/70`;
    }

    return '';
  }

  public manufacturerLogo(vehicle: Vehicle) {
    if (vehicle !== null) {
      return `//image.uk-tla.com/api/v1/image/vehicle/${vehicle.vehicleType}/${vehicle.manufacturerNameSlug}/logo/62`;
    }

    return '';
  }

  @Watch('xsaleVehicles')
  public showLoader() {
    if (this.xsaleVehicles.length > 0) {
      this.showLoadingIcon = false;
    }
  }

  public created() {
    this.showLoader();
    this.fetchSimilarVehicles();
  }
}
</script>
